// Flattens one raw ExpiringLeasesService record into the shape the table
// rows, headers and filters read. Ports `useAssignedUnitData` from the
// legacy `hooks.js`, with two deviations the conversion plan requires:
//
// - It never writes back into the record it was handed. The legacy hook
//   mutates `primaryResident.residentCustomer.customer.pp` in place; this
//   reads a fallback value instead.
// - A lease with no residents gets empty resident fields instead of
//   throwing. `getPrimaryResident` returns `undefined` for an empty
//   resident list, and the legacy hook then throws trying to write onto
//   that `undefined` value.
import { useMemo } from 'react';
import { getPrimaryResident, getLatestRenewalNote } from './utils';
import {
  getDaysUntilLeaseEnd,
  getRenewalStatusLabel,
  ON_NOTICE_TEXT,
} from './utilsV2';

const EMPTY_RENEWAL_NOTE = {
  notes: null,
  by: { firstName: null },
  cs: { isCompleted: false },
  startTime: null,
};

export const parseAssignedUnit = (assignedUnit, today) => {
  const { id, lease, unit, lastLease, unitId } = assignedUnit;

  const { isRenewal = false, isRenewalComplete = false } = lastLease || {};

  const {
    id: leaseId,
    resident = [],
    startDate: leaseStartDate = null,
    endDate: leaseEndDate = null,
    actualMoveInDate = null,
    priorLeasedRent = null,
    leasedRent = null,
  } = lease || {};

  // A destructuring default (`= {}`) only applies when the value is
  // `undefined`, not `null`. `lease.household` and `unit.floorPlan` can
  // both come back `null` from the API, so each is defaulted with `?? {}`
  // before it is destructured, instead of relying on the inline default,
  // which would otherwise throw and blank the whole table (every row runs
  // through the same `useMemo`).
  const { doNotRenew = '', underEviction = '' } = lease?.household ?? {};

  const { unitStatus, number } = unit || {};

  const {
    id: floorPlanId,
    internalName,
    quotingRentAmount,
    marketRent: baseMarketRentAmount,
  } = unit?.floorPlan ?? {};

  const primaryResident = getPrimaryResident(resident);

  // Fallback `pp` value instead of the legacy write-back. `primaryResident`
  // is `undefined` for a lease with no residents, so every level below is
  // defaulted rather than assumed present.
  const pp = primaryResident?.residentCustomer?.customer?.pp ?? { atv: [] };
  const renewalNotes = pp.atv ?? [];

  const { id: primaryResidentId = null } = primaryResident || {};

  // Same `null`-vs-`undefined` gap as `household`/`floorPlan` above:
  // `residentCustomer`, `customer` and the first `opLedger` entry can each
  // come back `null`, so every level here is defaulted with `??` before
  // it is destructured.
  const customer = primaryResident?.residentCustomer?.customer ?? {};
  const { firstName = '', lastName = '' } = customer;
  const [ledgerEntry] = customer.opLedger ?? [{}];
  const { allSubjournalRunningBalance: balance = '' } = ledgerEntry ?? {};

  const selectedLease = {
    unit: { id, number },
    residentName: `${firstName} ${lastName}`,
    leaseId,
  };

  const statusName = getRenewalStatusLabel({ isRenewal, isRenewalComplete });

  // `getLatestRenewalNote` sorts its input in place. Passing a copy keeps
  // this function from mutating the raw record's nested notes array.
  const latestRenewalNote =
    renewalNotes.length > 0
      ? getLatestRenewalNote([...renewalNotes], leaseStartDate)
      : EMPTY_RENEWAL_NOTE;

  const {
    notes,
    cs: { isCompleted: isRenewalNoteComplete } = {},
    by: { firstName: assignedTo } = {},
    startTime: renewalNoteDate,
  } = latestRenewalNote;

  const disableNote = !latestRenewalNote || !isRenewalNoteComplete;

  return {
    // Fields ported verbatim from the legacy `useAssignedUnitData`.
    assignedTo,
    balance,
    baseMarketRentAmount,
    disableNote,
    doNotRenew,
    firstName,
    floorPlanId,
    internalName,
    isRenewal,
    isRenewalComplete,
    isRenewalNoteComplete,
    lastName,
    lease,
    notes,
    primaryResident,
    primaryResidentId,
    quotingRentAmount,
    renewalNoteDate,
    selectedLease,
    statusName,
    underEviction,
    unitId,
    unitStatus,

    // Flattened fields `HEADERS`/`PROPERTY_PATH_MAP` (constantsV2.js) read
    // by id, and the row-cell builder in hooksV2.js reads to shape cells.
    leaseRenewalStatus: statusName,
    unit: number,
    floorPlan: internalName,
    resident: primaryResidentId
      ? `${lastName.length ? `${lastName}, ` : ''}${firstName}`
      : '---',
    residentId: primaryResidentId,
    moveInDate: actualMoveInDate,
    leaseStartDate,
    leaseEndDate,
    daysUntilLeaseEnd: getDaysUntilLeaseEnd(leaseEndDate, today),
    noticeToVacate: unitStatus?.description === ON_NOTICE_TEXT,
    marketRent: baseMarketRentAmount,
    priorLeasedRent,
    currentLeasedRent: leasedRent,
    // Always rendered as a fixed dash (see hooksV2.js's row-cell builder) —
    // there is no source field for this column, matching the legacy CSV
    // and on-screen table, which both hardcode the literal '---'. Kept as
    // an explicit `null` field so `PROPERTY_PATH_MAP`'s sort/search path
    // for this column resolves to a real value instead of a missing key.
    otherUnitFees: null,
    quotingRent: quotingRentAmount,

    // Bundles the three fields `matchesRenewalStatus` (utilsV2.js) needs
    // into one path the library's per-column filter can resolve in a
    // single lookup. See hooksV2.js's `PROPERTY_PATH_MAP` override.
    renewalStatusMeta: { unitStatus, isRenewal, isRenewalComplete },
  };
};

export const useParseResults = ({ results, today }) =>
  useMemo(
    () =>
      Array.isArray(results)
        ? results.map((result) => parseAssignedUnit(result, today))
        : [],
    [results, today],
  );
